import { useQuery } from '@tanstack/react-query';
import { analysisApi } from '@/services/api/analysisApi';
import { FailureFilters, DEFAULT_FAILURE_FILTERS } from '@/types';

/**
 * Configuration constants for VBL analysis
 */
const VBL_ANALYSIS_CONFIG = {
  /** Default confidence level for parameter intervals */
  DEFAULT_CONFIDENCE_LEVEL: 0.95,
  /** Query cache configuration */
  CACHE: {
    STALE_TIME: 5 * 60 * 1000, // 5 minutes
    CACHE_TIME: 10 * 60 * 1000, // 10 minutes
  },
} as const;

/**
 * Hook for VBL (Vibration-Based Life) analysis using Johnson's method.
 *
 * Analyzes degradation time (detection to failure) instead of operation time.
 * VBL data is never censored since it only includes confirmed failures.
 *
 * @param filters - Failure filters to apply to the dataset
 * @param confidenceLevel - Confidence level for parameter intervals (0-1)
 * @returns Analysis results including parameters, metadata, and diversity metrics
 *
 * @example
 * ```typescript
 * const { analysis, isLoading, hasEnoughData } = useVblAnalysis(
 *   { componentsMajor: ['Gearbox'] },
 *   0.95
 * );
 * ```
 */
export function useVblAnalysis(
  filters: FailureFilters = DEFAULT_FAILURE_FILTERS,
  confidenceLevel: number = VBL_ANALYSIS_CONFIG.DEFAULT_CONFIDENCE_LEVEL,
  enabled = true
) {

  // The VBL (Johnson) computation now runs server-side at
  // POST /api/v2/analysis/vbl (Phase 2B). This hook is a thin fetch over that
  // endpoint; the response envelope maps 1:1 to the hook's return contract.
  //
  // We expose `isInitialLoading` as our `isLoading` so a disabled query
  // (enabled=false) does not register as "loading" — in v4 plain `isLoading`
  // stays true for disabled queries until data arrives, which would leave UI
  // spinners stuck on page mount.
  //
  // The client-only range/date filters (operationTimeMin/Max, dateFrom/To) are
  // now applied server-side, so — unlike the legacy client-compute path which
  // stripped them — they are part of the request body and MUST be part of the
  // cache key. Including the whole `filters` object (rather than stripping the
  // range fields) keeps results that differ only by range from colliding in the
  // cache. `confidenceLevel` is in the key for the same reason.
  const {
    data: response,
    isInitialLoading: isLoading,
    error,
  } = useQuery({
    queryKey: ['vbl', 'analysis', filters, confidenceLevel],
    queryFn: () => analysisApi.getVblAnalysis(filters, confidenceLevel),
    staleTime: VBL_ANALYSIS_CONFIG.CACHE.STALE_TIME,
    cacheTime: VBL_ANALYSIS_CONFIG.CACHE.CACHE_TIME,
    enabled,
  });

  return {
    analysis: response?.analysis ?? null,
    isLoading,
    error,
    hasEnoughData: response?.hasEnoughData ?? false,
    sampleSize: response?.sampleSize ?? 0,
    metadata: response?.metadata ?? null,
    diversityMetrics: response?.diversityMetrics ?? null,
  };
}
